import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { useNavigate } from 'react-router-dom';

const DashboardChart = ({ data }) => {
    const chartRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        let chart;

        if (chartRef.current && data) {
            // Initialize ECharts instance
            chart = echarts.init(chartRef.current);

            // Format data for chart
            const formatData = () => {
                // Check which type of data structure we have
                const isCombinedData = data.totalInhouse || data.totalBanners || data.totalProducts || data.totalStory || data.totalBlog;

                let allViewsData = [];
                let allClicksData = [];

                if (isCombinedData) {
                    // Handle case where we have a single data type (products, inhouse, banners, story, or blog)
                    if (data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalStory && !data.totalBlog) {
                        // When only product data is selected
                        allViewsData = [...(data.totalProducts.views_data || [])];
                        allClicksData = [...(data.totalProducts.clicks_data || [])];
                    }
                    else if (data.totalInhouse && !data.totalProducts && !data.totalBanners && !data.totalStory && !data.totalBlog) {
                        // When only inhouse data is selected - show both views and clicks
                        allViewsData = [...(data.totalInhouse.views_data || [])];
                        allClicksData = [...(data.totalInhouse.clicks_data || [])];
                    }
                    else if (data.totalBanners && !data.totalProducts && !data.totalInhouse && !data.totalStory && !data.totalBlog) {
                        // When only banner data is selected
                        allViewsData = [...(data.totalBanners.views_data || [])];
                        allClicksData = [...(data.totalBanners.clicks_data || [])];
                    }
                    else if (data.totalStory && !data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalBlog) {
                        // When only story data is selected
                        allViewsData = [...(data.totalStory.views_data || [])];
                        allClicksData = [...(data.totalStory.clicks_data || [])];
                    }
                    else if (data.totalBlog && !data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalStory) {
                        // When only blog data is selected - show both views and clicks
                        allViewsData = [...(data.totalBlog.views_data || [])];
                        allClicksData = [...(data.totalBlog.clicks_data || [])];
                    }
                    else {
                        // When multiple data types or "all" is selected
                        if (data.totalInhouse) {
                            allViewsData = [...allViewsData, ...(data.totalInhouse.views_data || [])];
                            allClicksData = [...allClicksData, ...(data.totalInhouse.clicks_data || [])];
                        }

                        if (data.totalStory) {
                            allViewsData = [...allViewsData, ...(data.totalStory.views_data || [])];
                            allClicksData = [...allClicksData, ...(data.totalStory.clicks_data || [])];
                        }

                        if (data.totalBanners) {
                            allViewsData = [...allViewsData, ...(data.totalBanners.views_data || [])];
                            allClicksData = [...allClicksData, ...(data.totalBanners.clicks_data || [])];
                        }

                        if (data.totalProducts) {
                            allViewsData = [...allViewsData, ...(data.totalProducts.views_data || [])];
                            allClicksData = [...allClicksData, ...(data.totalProducts.clicks_data || [])];
                        }

                        if (data.totalBlog) {
                            allViewsData = [...allViewsData, ...(data.totalBlog.views_data || [])];
                            allClicksData = [...allClicksData, ...(data.totalBlog.clicks_data || [])];
                        }
                    }
                } else {
                    // Handle the simple data format
                    allViewsData = data.views_data || [];
                    allClicksData = data.clicks_data || [];
                }

                // Get all unique dates from both datasets
                const allDates = [...new Set([
                    ...allViewsData.map(item => item.createdAt.split('T')[0]),
                    ...allClicksData.map(item => item.createdAt.split('T')[0])
                ])].sort();

                // Create mapping of dates to counts
                const viewsMap = allViewsData.reduce((acc, item) => {
                    const date = item.createdAt.split('T')[0];
                    acc[date] = (acc[date] || 0) + item.count;
                    return acc;
                }, {});

                const clicksMap = allClicksData.reduce((acc, item) => {
                    const date = item.createdAt.split('T')[0];
                    acc[date] = (acc[date] || 0) + item.count;
                    return acc;
                }, {});

                // Create series data with 0 for missing dates
                const viewsSeries = allDates.map(date => viewsMap[date] || 0);
                const clicksSeries = allDates.map(date => clicksMap[date] || 0);

                return {
                    dates: allDates,
                    viewsSeries,
                    clicksSeries
                };
            };

            const { dates, viewsSeries, clicksSeries } = formatData();

            // If no dates, show a message in the chart
            if (dates.length === 0) {
                chart.setOption({
                    title: {
                        text: 'No data available for the selected period',
                        left: 'center',
                        top: 'center',
                        textStyle: {
                            color: '#999',
                            fontSize: 16,
                            fontWeight: 'normal'
                        }
                    }
                });
                return;
            }

            // Determine which series to show based on data type
            const determineSeries = () => {
                // Default series (both views and clicks)
                const defaultSeries = [
                    {
                        name: 'Views',
                        type: 'line',
                        smooth: true,
                        symbol: 'circle',
                        symbolSize: 6,
                        itemStyle: {
                            color: '#7F00FF'
                        },
                        lineStyle: {
                            width: 3,
                            color: '#7F00FF'
                        },
                        areaStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: 'rgba(127, 0, 255, 0.4)' },
                                { offset: 1, color: 'rgba(127, 0, 255, 0.1)' }
                            ])
                        },
                        data: viewsSeries
                    },
                    {
                        name: 'Clicks',
                        type: 'line',
                        smooth: true,
                        symbol: 'circle',
                        symbolSize: 6,
                        itemStyle: {
                            color: '#A81C17'
                        },
                        lineStyle: {
                            width: 3,
                            color: '#A81C17'
                        },
                        areaStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: 'rgba(168, 28, 23, 0.4)' },
                                { offset: 1, color: 'rgba(168, 28, 23, 0.1)' }
                            ])
                        },
                        data: clicksSeries
                    }
                ];

                // Only product data (views only)
                if (data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalStory && !data.totalBlog) {
                    return [defaultSeries[0]]; // Views only
                }
                // Only story data (views only)
                else if (data.totalStory && !data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalBlog) {
                    return [defaultSeries[0]]; // Views only
                }
                // Only blog data (views only)
                else if (data.totalBlog && !data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalStory) {
                    return [defaultSeries[0]]; // Views only
                }
                // Only banner data (clicks only)
                else if (data.totalBanners && !data.totalProducts && !data.totalInhouse && !data.totalStory && !data.totalBlog) {
                    return [defaultSeries[1]]; // Clicks only
                }
                // Inhouse data or All data types or combined
                else {
                    return defaultSeries; // Both views and clicks
                }
            };

            // Determine legend data based on data type
            const determineLegend = () => {
                if (data.totalBanners && !data.totalProducts && !data.totalInhouse && !data.totalStory && !data.totalBlog) {
                    return ['Clicks']; // Only clicks for banner data
                } else if ((data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalStory && !data.totalBlog) ||
                    (data.totalStory && !data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalBlog) ||
                    (data.totalBlog && !data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalStory)) {
                    return ['Views']; // Only views for product, story or blog data
                } else {
                    return ['Views', 'Clicks']; // Both for inhouse and combined data
                }
            };

            // Chart options
            const option = {
                backgroundColor: '#fff',
                color: ['#7F00FF', '#A81C17'],
                tooltip: {
                    trigger: 'axis',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderColor: '#e6e6e6',
                    borderWidth: 1,
                    textStyle: {
                        color: '#333'
                    },
                    formatter: function (params) {
                        let result = `<div style="font-weight: bold; margin-bottom: 5px;">${params[0].axisValue}</div>`;
                        params.forEach(param => {
                            const color = param.seriesName === 'Views' ? '#7F00FF' : '#A81C17';
                            result += `<div style="display: flex; align-items: center; margin: 5px 0;">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${color}; margin-right: 5px;"></span>
                <span style="margin-right: 15px;">${param.seriesName}:</span>
                <span style="font-weight: bold;">${param.value}</span>
              </div>`;
                        });
                        return result;
                    }
                },
                legend: {
                    data: determineLegend(),
                    bottom: 0,
                    icon: 'circle',
                    itemWidth: 10,
                    itemHeight: 10,
                    textStyle: {
                        color: '#666'
                    }
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '15%',
                    top: '10%',
                    containLabel: true
                },
                xAxis: {
                    type: 'category',
                    boundaryGap: false,
                    data: dates.map(date => {
                        const d = new Date(date);
                        return `${d.getDate()}/${d.getMonth() + 1}`;
                    }),
                    axisLine: {
                        lineStyle: {
                            color: '#ddd'
                        }
                    },
                    axisTick: {
                        show: false
                    },
                    axisLabel: {
                        color: '#666',
                        fontSize: 12
                    }
                },
                yAxis: {
                    type: 'value',
                    splitLine: {
                        lineStyle: {
                            type: 'dashed',
                            color: '#eee'
                        }
                    },
                    axisLine: {
                        show: false
                    },
                    axisTick: {
                        show: false
                    },
                    axisLabel: {
                        color: '#666',
                        fontSize: 12
                    }
                },
                series: determineSeries()
            };

            // Set chart options and render
            chart.setOption(option);

            // Handle window resize
            const handleResize = () => {
                chart.resize();
            };

            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                chart.dispose();
            };
        }
    }, [data]);

    // Display summary statistics based on data type
    const renderSummary = () => {
        if (!data) return null;

        let totalViews = 0;
        let totalClicks = 0;

        // Only product data is selected (views only)
        if (data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalStory && !data.totalBlog) {
            totalViews = (data.totalProducts.views_data || []).reduce((sum, item) => sum + item.count, 0);

            return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-main-blue/10 rounded-lg p-4 flex flex-col cursor-pointer hover:scale-105 transition" onClick={() => navigate('/manage/list')}>
                        <span className="text-main-blue text-sm font-medium">Product Views</span>
                        <span className="text-2xl font-bold text-main-blue">{totalViews}</span>
                    </div>
                </div>
            );
        }
        // Only story data is selected (views only)
        else if (data.totalStory && !data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalBlog) {
            totalViews = (data.totalStory.views_data || []).reduce((sum, item) => sum + item.count, 0);

            return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-main-blue/10 rounded-lg p-4 flex flex-col cursor-pointer hover:scale-105 transition" onClick={() => navigate('/manage/story')}>
                        <span className="text-main-blue text-sm font-medium">Story Views</span>
                        <span className="text-2xl font-bold text-main-blue">{totalViews}</span>
                    </div>
                </div>
            );
        }
        // Only inhouse data is selected (both views and clicks)
        else if (data.totalInhouse && !data.totalProducts && !data.totalBanners && !data.totalStory && !data.totalBlog) {
            totalViews = (data.totalInhouse.views_data || []).reduce((sum, item) => sum + item.count, 0);
            totalClicks = (data.totalInhouse.clicks_data || []).reduce((sum, item) => sum + item.count, 0);

            return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-main-blue/10 rounded-lg p-4 flex flex-col cursor-pointer hover:scale-105 transition" onClick={() => navigate('/manage/ads/inHouseAds/add')}>
                        <span className="text-main-blue text-sm font-medium">Inhouse Views</span>
                        <span className="text-2xl font-bold text-main-blue">{totalViews}</span>
                    </div>
                    <div className="bg-main-red/10 rounded-lg p-4 flex flex-col cursor-pointer hover:scale-105 transition" onClick={() => navigate('/manage/ads/inHouseAds/add')}>
                        <span className="text-main-red text-sm font-medium">Inhouse Clicks</span>
                        <span className="text-2xl font-bold text-main-red">{totalClicks}</span>
                    </div>
                </div>
            );
        }
        // Only banner data is selected (clicks only)
        else if (data.totalBanners && !data.totalInhouse && !data.totalProducts && !data.totalStory && !data.totalBlog) {
            totalClicks = (data.totalBanners.clicks_data || []).reduce((sum, item) => sum + item.count, 0);

            return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-main-red/10 rounded-lg p-4 flex flex-col cursor-pointer hover:scale-105 transition" onClick={() => navigate('/manage/banner')}>
                        <span className="text-main-red text-sm font-medium">Banner Clicks</span>
                        <span className="text-2xl font-bold text-main-red">{totalClicks}</span>
                    </div>
                </div>
            );
        }
        // Only blog data is selected (views only)
        else if (data.totalBlog && !data.totalInhouse && !data.totalProducts && !data.totalBanners && !data.totalStory) {
            totalViews = (data.totalBlog.views_data || []).reduce((sum, item) => sum + item.count, 0);

            return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-main-blue/10 rounded-lg p-4 flex flex-col cursor-pointer hover:scale-105 transition" onClick={() => navigate('/manage/blog')}>
                        <span className="text-main-blue text-sm font-medium">Blog Views</span>
                        <span className="text-2xl font-bold text-main-blue">{totalViews}</span>
                    </div>
                </div>
            );
        }
        // Multiple data types are selected ("all" view or combination)
        else {
            if (data.totalInhouse) {
                totalViews += (data.totalInhouse.views_data || []).reduce((sum, item) => sum + item.count, 0);
                totalClicks += (data.totalInhouse.clicks_data || []).reduce((sum, item) => sum + item.count, 0);
            }

            if (data.totalProducts) {
                totalViews += (data.totalProducts.views_data || []).reduce((sum, item) => sum + item.count, 0);
                totalClicks += (data.totalProducts.clicks_data || []).reduce((sum, item) => sum + item.count, 0);
            }

            if (data.totalBanners) {
                totalViews += (data.totalBanners.views_data || []).reduce((sum, item) => sum + item.count, 0);
                totalClicks += (data.totalBanners.clicks_data || []).reduce((sum, item) => sum + item.count, 0);
            }

            if (data.totalStory) {
                totalViews += (data.totalStory.views_data || []).reduce((sum, item) => sum + item.count, 0);
                totalClicks += (data.totalStory.clicks_data || []).reduce((sum, item) => sum + item.count, 0);
            }

            if (data.totalBlog) {
                totalViews += (data.totalBlog.views_data || []).reduce((sum, item) => sum + item.count, 0);
                totalClicks += (data.totalBlog.clicks_data || []).reduce((sum, item) => sum + item.count, 0);
            }

            return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-main-blue/10 rounded-lg p-4 flex flex-col transition">
                        <span className="text-main-blue text-sm font-medium">Total Views</span>
                        <span className="text-2xl font-bold text-main-blue">{totalViews}</span>
                    </div>
                    <div className="bg-main-red/10 rounded-lg p-4 flex flex-col transition">
                        <span className="text-main-red text-sm font-medium">Total Clicks</span>
                        <span className="text-2xl font-bold text-main-red">{totalClicks}</span>
                    </div>
                </div>
            );
        }
    };

    // Get the appropriate title and footer based on data type
    const getTitle = () => {
        if (data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalStory && !data.totalBlog) {
            return "Product Data Engagement";
        } else if (data.totalInhouse && !data.totalProducts && !data.totalBanners && !data.totalStory && !data.totalBlog) {
            return "Inhouse Data Engagement";
        } else if (data.totalBanners && !data.totalProducts && !data.totalInhouse && !data.totalStory && !data.totalBlog) {
            return "Banner Data Engagement";
        } else if (data.totalStory && !data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalBlog) {
            return "Story Data Engagement";
        } else if (data.totalBlog && !data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalStory) {
            return "Blog Data Engagement";
        } else if (data.totalInhouse || data.totalBanners || data.totalProducts || data.totalStory || data.totalBlog) {
            return "Combined Data Engagement";
        } else {
            return "Product Engagement";
        }
    };

    const getFooter = () => {
        // Only product data is selected (views only)
        if (data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalStory && !data.totalBlog) {
            return (
                <div className="mt-4 text-sm text-gray-500">
                    <p>* Product Views represent visitors viewing product details</p>
                </div>
            );
        }
        // Only story data is selected (views only)
        else if (data.totalStory && !data.totalProducts && !data.totalInhouse && !data.totalBanners && !data.totalBlog) {
            return (
                <div className="mt-4 text-sm text-gray-500">
                    <p>* Story Views represent visitors viewing story content</p>
                </div>
            );
        }
        // Only inhouse data is selected (both views and clicks)
        else if (data.totalInhouse && !data.totalProducts && !data.totalBanners && !data.totalStory && !data.totalBlog) {
            return (
                <div className="mt-4 text-sm text-gray-500">
                    <p>* Inhouse Views represent visitors viewing internal content</p>
                    <p>* Inhouse Clicks represent interactions with internal content</p>
                </div>
            );
        }
        // Only banner data is selected (clicks only)
        else if (data.totalBanners && !data.totalInhouse && !data.totalProducts && !data.totalStory && !data.totalBlog) {
            return (
                <div className="mt-4 text-sm text-gray-500">
                    <p>* Banner Clicks represent interactions with promotional banners</p>
                </div>
            );
        }
        // Only blog data is selected (views only)
        else if (data.totalBlog && !data.totalInhouse && !data.totalProducts && !data.totalBanners && !data.totalStory) {
            return (
                <div className="mt-4 text-sm text-gray-500">
                    <p>* Blog Views represent visitors viewing blog content</p>
                </div>
            );
        }
        // Multiple data types are selected ("all" view or combination)
        else {
            return (
                <div className="mt-4 text-sm text-gray-500">
                    <p>* Views represent visitors viewing content (products, stories, inhouse, and blogs)</p>
                    <p>* Clicks represent interactions with promotional content</p>
                </div>
            );
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-main-border overflow-hidden p-6 mt-6">
            <h3 className="text-lg font-semibold text-main-font mb-4">{getTitle()}</h3>

            {renderSummary()}

            <div ref={chartRef} style={{ width: '100%', height: '400px' }}></div>

            {getFooter()}
        </div>
    );
};

export default DashboardChart;